import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export interface TelegramSettings {
  id: number;
  bot_token: string | null;
  chat_id: string | null;
  notify_booking: boolean;
  notify_discount_expiry: boolean;
  expiry_days_threshold: number;
}

export interface TelegramSettingsInput {
  bot_token: string;
  chat_id: string;
  notify_booking: boolean;
  notify_discount_expiry: boolean;
  expiry_days_threshold: number;
}

export function useTelegramSettings() {
  const [settings, setSettings] = useState<TelegramSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('vietnamtravel_telegram_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (err) throw err;
      setSettings((data as TelegramSettings) || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Telegram settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async (input: TelegramSettingsInput) => {
    const { error: err } = await supabase.from('vietnamtravel_telegram_settings').upsert({
      id: 1,
      bot_token: input.bot_token.trim() || null,
      chat_id: input.chat_id.trim() || null,
      notify_booking: input.notify_booking,
      notify_discount_expiry: input.notify_discount_expiry,
      expiry_days_threshold: input.expiry_days_threshold,
      updated_at: new Date().toISOString(),
    });
    if (err) return { error: err.message };
    await fetchSettings();
    return { error: null };
  };

  const sendTestMessage = async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/vietnamtravel-send-telegram`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'test' }),
      });
      const result = await res.json().catch(() => null);
      if (!res.ok) {
        return {
          error: result.error || `Server error ${res.status}: ${res.statusText}`,
        };
      }
      if (!result.ok) {
        return { error: result.error || 'Unknown error from Telegram service.' };
      }
      return { error: null };
    } catch (err) {
      return {
        error: err instanceof Error ? err.message : 'Failed to connect to Telegram service.',
      };
    }
  };

  return {
    settings,
    loading,
    error,
    refresh: fetchSettings,
    saveSettings,
    sendTestMessage,
  };
}